import React, { useState, useEffect } from "react";
import { Tooltip } from "@mui/material";

const RHChipInput = ({ 
  label, 
  name, 
  value, 
  onChange, 
  required = false, 
  disabled = false, 
  placeholder,
  description,
  hasError = false,
  sx = {}
}) => {
  const [inputValue, setInputValue] = useState("");
  const [chips, setChips] = useState([]);

  useEffect(() => {
    // Ensure value is always treated as an array
    setChips(Array.isArray(value) ? value : value ? [value] : []);
  }, [value]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    if (inputValue.trim()) {
      const newChips = [...chips, inputValue.trim()];
      setChips(newChips);
      setInputValue("");
      // Create a synthetic event for compatibility with existing form handlers
      const syntheticEvent = {
        target: {
          name: name,
          value: newChips,
          type: 'chip'
        }
      };
      onChange(syntheticEvent);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Tab" || e.key === "Enter") {
      e.preventDefault();
      handleInputBlur();
    }
  };

  const removeChip = (index) => {
    const newChips = chips.filter((_, i) => i !== index);
    setChips(newChips);
    // Create a synthetic event for compatibility with existing form handlers
    const syntheticEvent = {
      target: {
        name: name,
        value: newChips,
        type: 'chip'
      }
    };
    onChange(syntheticEvent);
  };

  const containerStyles = {
    marginBottom: '16px',
    ...sx
  };

  const inputContainerStyles = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    padding: '8px',
    backgroundColor: hasError ? '#2d1b1b' : '#27272a', // zinc-800 with error state
    border: hasError ? '1px solid #dc2626' : '1px solid #52525b', // zinc-600 with error state
    borderRadius: '6px',
    minHeight: '42px',
    alignItems: 'center',
    '&:focus-within': {
      outline: '2px solid #8b5cf6', // violet-500
      outlineOffset: '2px'
    }
  };

  const chipStyles = {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#3f3f46', // zinc-700
    color: 'white',
    padding: '4px 8px',
    borderRadius: '6px',
    fontSize: '14px'
  };

  const removeButtonStyles = {
    marginLeft: '8px',
    color: '#a1a1aa', // zinc-400
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    lineHeight: '1',
    '&:hover': {
      color: 'white'
    },
    '&:focus': {
      outline: 'none'
    }
  };

  const inputStyles = {
    flexGrow: 1,
    minWidth: '120px',
    backgroundColor: 'transparent',
    color: 'white',
    border: 'none',
    outline: 'none',
    fontSize: '14px'
  };

  const labelStyles = {
    display: 'block',
    marginBottom: '4px',
    fontSize: '14px',
    fontWeight: '500',
    color: hasError ? '#dc2626' : '#f4f4f5' // zinc-100 with error state
  };

  return (
    <div style={containerStyles}>
      {label && (
        <label style={labelStyles}>
          {description ? (
            <Tooltip title={description} placement="top">
              <span>
                {label}
                {required && <span style={{ color: '#dc2626' }}> *</span>}
              </span>
            </Tooltip>
          ) : (
            <>
              {label}
              {required && <span style={{ color: '#dc2626' }}> *</span>}
            </>
          )}
        </label>
      )}
      <div style={inputContainerStyles}>
        {chips.map((chip, index) => (
          <div key={index} style={chipStyles}>
            <span>{chip}</span>
            {!disabled && (
              <button
                type="button"
                onClick={() => removeChip(index)}
                style={removeButtonStyles}
                onMouseEnter={(e) => e.target.style.color = 'white'}
                onMouseLeave={(e) => e.target.style.color = '#a1a1aa'}
              >
                ×
              </button>
            )}
          </div>
        ))}
        <input
          type="text"
          name={name}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          required={required && chips.length === 0}
          disabled={disabled}
          placeholder={placeholder || `Enter ${label || 'tags'}`}
          style={inputStyles}
        />
      </div>
    </div>
  );
};

export default RHChipInput;
